/**
 * What a party IS, as a strip of governed marks (#2267 port 5).
 *
 * ── what this replaces, and why it was wrong ────────────────────────────────
 * The legacy CRM record page carried `OrgRolePill`: it took `party_role` rows,
 * read `[0].role`, and coloured it from a THREE-ENTRY map typed in the page —
 * `customer` good, `prospect` warn, everything else neutral. Two defects in six
 * lines, and both are the reason this file is a derivation rather than a copy:
 *
 *   · **it showed one role.** A party wears several at once — an organisation
 *     that buys from us and supplies us is a customer AND a vendor, and the
 *     page showed whichever row the database happened to return first. Which
 *     one that is has no meaning, so the pill was a coin toss presented as a
 *     fact.
 *   · **it coloured from a map in the page.** `core.party_role_type` already
 *     carries `label`, `tone` and `sort` (core 0022) — governed data a
 *     deployment edits. A tenant that renames "prospect" or adds "reseller" got
 *     `titleCase(key)` in neutral, and nothing said so.
 *
 * So: every ACTIVE role, in the catalogue's own order, under the catalogue's own
 * label and tone. Nothing here decides what a role means.
 *
 * ── the catalogue is read UNFILTERED, deliberately ──────────────────────────
 * Same load-bearing line as `PartyRelations` (#1075): an ARCHIVED role type must
 * still resolve its real label on an assignment that already carries it.
 * Filtering the catalogue to `status = 'active'` is a one-line edit that
 * silently degrades a historical role to `titleCase(key)` in neutral — which
 * reads as a value nobody recognises rather than as a retired one. Availability
 * governs what may be ADDED (`availableOrganizationRoles`), never how an
 * existing assignment is spelled.
 */
import type { Row } from '@solum/sdk'
import type { Tone } from '@solum/ui'
import { roleTypeLabel } from '../app/partyRoles'

export interface PartyIdentityBadge {
  /** The role key — the identity, and the React key. */
  key: string
  label: string
  tone: Tone
}

/** The tones `@solum/ui` actually renders. An unrecognised registry value is
 *  neutral rather than a crash or an invented colour. */
const TONES = new Set(['good', 'warn', 'critical', 'neutral', 'brand'])
const toneOf = (value: unknown): Tone =>
  (TONES.has(String(value)) ? (String(value) as Tone) : 'neutral')

/**
 * Every active role this party wears, spelled by the catalogue.
 *
 * `roles` are `core.party_role` rows already scoped to one party; `roleTypes`
 * is the UNFILTERED `core.party_role_type` catalogue.
 *
 * Order is the catalogue's `sort`, then its key — never the order the rows
 * arrived in. A role type the catalogue does not know keeps its key as a label
 * and sorts last: a governed value appearing under its raw key is a registration
 * somebody fixes, and one that silently vanishes is a party that reads as less
 * than it is.
 */
export function partyIdentityBadges(roles: Row[], roleTypes: Row[]): PartyIdentityBadge[] {
  const catalogue = new Map(roleTypes.map((type) => [String(type.key), type] as const))
  const seen = new Set<string>()
  return roles
    .filter((role) => role.status === 'active')
    .map((role) => String(role.role))
    .filter((key) => key !== '' && !seen.has(key) && (seen.add(key), true))
    .map((key) => {
      const type = catalogue.get(key)
      return {
        key,
        label: roleTypeLabel(type, key),
        tone: toneOf(type?.tone),
        sort: type ? Number(type.sort ?? 0) : Number.MAX_SAFE_INTEGER,
      }
    })
    .sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key))
    .map(({ key, label, tone }) => ({ key, label, tone }))
}
